import React, { useEffect, useState } from "react";
import {
  IproductRegistration,
  IRegisteredUserWithDetails,
} from "../../models/epcCustomer.model";
import Button, { ButtonType } from "../Assets/Button";
import toast from "react-hot-toast";
import hasChanges from "../../utils/registeredProductUtils";
import {
  generateModelDraftAgreement,
  generateProvisionalAgreement,
  generateProvisionalAgreementTorrent,
} from "../../utils/wordDocs.util";
import {
  Copy,
  FileDown,
  Loader2,
  MapPin,
  User,
  Zap,
  FileText,
  BadgeCheck,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { InverterSerialNumbersPdf } from "./InverterSerialNumbersPdf";
import { SelfCertificationPdf } from "./SelfCertificationPdf";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

const PANEL_MAKES = [
  { value: "ADANI", label: "ADANI" },
  { value: "RAYZON", label: "RAYZON" },
  { value: "WAAREE", label: "WAAREE" },
  { value: "GREEN BRILLIANCE", label: "GREEN BRILLIANCE" },
];

const PANEL_CAPACITY_MAPPING: Record<
  string,
  { value: string; label: string }[]
> = {
  ADANI: [
    { value: "550 WP", label: "550 WP" },
    { value: "615 WP", label: "615 WP" },
  ],
  RAYZON: [
    { value: "545 WP", label: "545 WP" },
    { value: "620 WP", label: "620 WP" },
  ],
  WAAREE: [
    { value: "540 WP", label: "540 WP" },
    { value: "580 WP", label: "580 WP" },
  ],
  "GREEN BRILLIANCE": [
    { value: "550 WP", label: "550 WP" },
    { value: "620 WP", label: "620 WP" },
  ],
};

const INVERTER_MAKES = [{ value: "FRELIT", label: "FRELIT" }];

const INVERTER_CAPACITIES = [
  { value: "3.0kW", label: "3.0kW" },
  { value: "4.0kW", label: "4.0kW" },
  { value: "5.0kW", label: "5.0kW" },
  { value: "6.0kW", label: "6.0kW" },
  { value: "8.0kW", label: "8.0kW" },
  { value: "10.0kW", label: "10.0kW" },
];

interface ShowregisteredProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  productRegistration?: IproductRegistration | null;
  customer?: IRegisteredUserWithDetails | null;
  onUpdate?: (
    updatedData: IproductRegistration,
    geoPhotoFile?: File | null,
  ) => Promise<any> | void;
  isUpdating?: boolean;
}

interface SmartInputProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}

const SmartInput: React.FC<SmartInputProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}) => {
  const isPredefined = options.some((opt) => opt.value === value);
  const showCustomInput = value && !isPredefined;

  const selectValue = showCustomInput ? "Other" : value || "";

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-semibold text-gray-600 block">
        {label}
      </label>

      {/* The Dropdown */}
      <select
        value={selectValue}
        disabled={disabled}
        onChange={(e) => {
          const val = e.target.value;
          if (val === "Other") {
            onChange("");
          } else {
            onChange(val);
          }
        }}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
      >
        <option value="" disabled>
          Select {label}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        <option value="Other">Other / Manual Entry</option>
      </select>

      {(selectValue === "Other" || (!selectValue && !value)) && (
        <input
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || `Enter ${label} manually`}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 animate-in fade-in slide-in-from-top-1 duration-200"
        />
      )}
    </div>
  );
};

const ShowRegisteredProductModal: React.FC<ShowregisteredProductModalProps> = ({
  isOpen,
  onClose,
  productRegistration,
  customer,
  onUpdate,
  isUpdating,
}) => {
  const [isEdit, setIsEdit] = useState(false);

  const [name, setName] = useState<string>("");
  const [consumerNumber, setConsumerNumber] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [area, setArea] = useState<string>("");
  const [registrationNumber, setRegistrationNumber] = useState<string>("");

  const [panelMake, setPanelMake] = useState<string>("");
  const [panelCapacity, setPanelCapacity] = useState<string>("");
  const [numberOfModules, setNumberOfModules] = useState<string>("");
  const [totalSolarCapacity, setTotalSolarCapacity] = useState<string>("");
  const [panelVoltage, setPanelVoltage] = useState<string>("");

  const [inverterMake, setInverterMake] = useState<string>("");
  const [inverterCapacity, setInverterCapacity] = useState<string>("");
  const [inverterVoltage, setInverterVoltage] = useState<string>("");

  const [panelText, setPanelText] = useState<string>("");
  const [inverterText, setInverterText] = useState<string>("");
  const [installationDate, setInstallationDate] = useState<string>("");

  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(false);

  const currentConsumerNumber = productRegistration?.consumerNumber;
  const canGenerate = currentConsumerNumber?.trim() !== "";
  const loading = isUpdating || saving;
  const inverterSize = customer?.quote?.plantSize;

  const role = useSelector((state: RootState) => state.auth.role);

  useEffect(() => {
    if (!isOpen) return;
    setIsEdit(false);
    setSaving(false);
    setFile(null);

    setName(productRegistration?.name || "");
    setConsumerNumber(productRegistration?.consumerNumber || "");
    setAddress(productRegistration?.address || "");
    setArea(productRegistration?.area || "");
    setRegistrationNumber(productRegistration?.registrationNumber || "");

    setPanelMake(productRegistration?.panelMake || "");
    setPanelCapacity(productRegistration?.panelCapacity || "");
    setNumberOfModules(productRegistration?.numberOfModules || "");
    setTotalSolarCapacity(productRegistration?.totalSolarCapacity || "");
    setPanelVoltage(productRegistration?.panelVoltage || "");

    setInverterMake(productRegistration?.inverterMake || "");
    setInverterCapacity(productRegistration?.inverterCapacity || "");
    setInverterVoltage(productRegistration?.inverterVoltage || "");

    setPanelText((productRegistration?.panelBarcodes || []).join("\n") || "");
    setInverterText(
      (productRegistration?.inverterBarcodes || []).join("\n") || "",
    );
    const isoDate = (productRegistration as any)?.installationDate;
    setInstallationDate(
      isoDate ? new Date(isoDate).toISOString().split("T")[0] : "",
    );
  }, [isOpen, productRegistration]);

  if (!isOpen) return null;

  const parseLines = (text: string) =>
    text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

  const isAlphanumeric = (str: string) => /^[a-zA-Z0-9]+$/.test(str);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const cancelEdit = () => {
    setIsEdit(false);
    setFile(null);
    setName(productRegistration?.name || "");
    setConsumerNumber(productRegistration?.consumerNumber || "");
    setAddress(productRegistration?.address || "");
    setArea(productRegistration?.area || "");
    setRegistrationNumber(productRegistration?.registrationNumber || "");

    setPanelMake(productRegistration?.panelMake || "");
    setPanelCapacity(productRegistration?.panelCapacity || "");
    setNumberOfModules(productRegistration?.numberOfModules || "");
    setTotalSolarCapacity(productRegistration?.totalSolarCapacity || "");
    setPanelVoltage(productRegistration?.panelVoltage || "");

    setInverterMake(productRegistration?.inverterMake || "");
    setInverterCapacity(productRegistration?.inverterCapacity || "");
    setInverterVoltage(productRegistration?.inverterVoltage || "");

    setPanelText((productRegistration?.panelBarcodes || []).join("\n"));
    setInverterText((productRegistration?.inverterBarcodes || []).join("\n"));
    const isoDate = (productRegistration as any)?.installationDate;
    setInstallationDate(
      isoDate ? new Date(isoDate).toISOString().split("T")[0] : "",
    );
  };

  const removeBarcode = (type: "panel" | "inverter", index: number) => {
    if (type === "panel") {
      const arr = parseLines(panelText);
      arr.splice(index, 1);
      setPanelText(arr.join("\n"));
    } else {
      const arr = parseLines(inverterText);
      arr.splice(index, 1);
      setInverterText(arr.join("\n"));
    }
  };

  const copyToClipboard = async (text: string) => {
    if (!text) {
      toast.error("Nothing to copy");
      return;
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      toast.success("Copied!");
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const handleDownload = () => {
    if (!productRegistration?.geoPhoto?.link) return;

    const fileUrl = productRegistration.geoPhoto.link;
    const fileName = productRegistration.geoPhoto.name;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", fileName);
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSave = async () => {
    if (!onUpdate) {
      setIsEdit(false);
      return;
    }

    const currentPanelBarcodes = parseLines(panelText);
    const currentInverterBarcodes = parseLines(inverterText);

    const invalidPanelCodes = currentPanelBarcodes.filter(
      (c) => !isAlphanumeric(c),
    );
    if (invalidPanelCodes.length > 0) {
      toast.error(
        `Invalid panel barcodes (alphanumeric only): ${invalidPanelCodes.join(
          ", ",
        )}`,
      );
      return;
    }
    const invalidInverterCodes = currentInverterBarcodes.filter(
      (c) => !isAlphanumeric(c),
    );
    if (invalidInverterCodes.length > 0) {
      toast.error(
        `Invalid inverter barcodes (alphanumeric only): ${invalidInverterCodes.join(
          ", ",
        )}`,
      );
      return;
    }

    const uniquePanels = new Set(currentPanelBarcodes);
    if (uniquePanels.size !== currentPanelBarcodes.length) {
      toast.error("Duplicate panel barcodes detected.");
      return;
    }
    const uniqueInverters = new Set(currentInverterBarcodes);
    if (uniqueInverters.size !== currentInverterBarcodes.length) {
      toast.error("Duplicate inverter barcodes detected.");
      return;
    }

    const maxModules = parseInt(numberOfModules, 10);
    if (!isNaN(maxModules) && currentPanelBarcodes.length !== maxModules) {
      toast.error(
        `Mismatch: Number of modules is ${maxModules}, but you have ${currentPanelBarcodes.length} panel barcodes.`,
      );
      return;
    }

    const updated: IproductRegistration = {
      name: name?.trim() || undefined,
      consumerNumber: consumerNumber?.trim() || undefined,
      address: address?.trim(),
      area: area?.trim(),
      registrationNumber: registrationNumber?.trim() || "",

      panelMake: panelMake?.trim(),
      panelCapacity: panelCapacity?.trim(),
      numberOfModules: numberOfModules?.trim(),
      totalSolarCapacity: totalSolarCapacity?.trim() || "",
      panelVoltage: panelVoltage?.trim() || "",

      inverterMake: inverterMake?.trim(),
      inverterCapacity: inverterCapacity?.trim(),
      inverterVoltage: inverterVoltage?.trim() || "",

      panelBarcodes: currentPanelBarcodes,
      inverterBarcodes: currentInverterBarcodes,
      installationDate: installationDate
        ? new Date(installationDate).toISOString()
        : undefined,
    };

    if (!hasChanges(productRegistration, updated, file)) {
      toast("No changes detected");
      return;
    }

    try {
      setSaving(true);
      await onUpdate(updated, file);
      setSaving(false);
      setIsEdit(false);
      onClose();
    } catch (err) {
      console.error("Failed to update product registration:", err);
      setSaving(false);
    }
  };

  const validateAndAddBarcode = (
    type: "panel" | "inverter",
    inputVal: string,
    currentText: string,
    setText: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    const val = inputVal.trim();
    if (!val) return;

    if (!isAlphanumeric(val)) {
      toast.error("Barcodes must be alphanumeric only.");
      return;
    }

    const currentList = parseLines(currentText);
    if (
      currentList.some((barcode) => barcode.toLowerCase() === val.toLowerCase())
    ) {
      toast.error("This barcode is already added.");
      return;
    }

    if (type === "panel") {
      const maxModules = parseInt(numberOfModules, 10);
      if (!numberOfModules || isNaN(maxModules) || maxModules <= 0) {
        toast.error(
          "Please set a valid 'Number of Modules' before adding barcodes.",
        );
        return;
      }
      if (currentList.length >= maxModules) {
        toast.error(`Cannot add more than ${maxModules} panel barcodes.`);
        return;
      }
    }

    currentList.push(val);
    setText(currentList.join("\n"));
  };

  const addBarcodeInput = (
    type: "panel" | "inverter",
    text: string,
    setText: React.Dispatch<React.SetStateAction<string>>,
  ) => (
    <div className="flex gap-2 mb-2">
      <input
        type="text"
        placeholder={`Scan/Type ${type === "panel" ? "Panel" : "Inverter"} Barcode`}
        className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            validateAndAddBarcode(type, e.currentTarget.value, text, setText);
            if (isAlphanumeric(e.currentTarget.value.trim())) {
              e.currentTarget.value = "";
            }
          }
        }}
      />
      <button
        className="px-3 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium"
        onClick={(e) => {
          const input = e.currentTarget.previousSibling as HTMLInputElement;
          validateAndAddBarcode(type, input.value, text, setText);
          input.value = "";
        }}
      >
        Add
      </button>
    </div>
  );

  type Discom = "MGVCL" | "PGVCL" | "UGVCL" | "DGVCL";

  const discomButtons: {
    label: Discom;
    color: "green" | "purple" | "yellow" | "red";
  }[] = [
    { label: "MGVCL", color: "green" },
    { label: "PGVCL", color: "purple" },
    { label: "UGVCL", color: "yellow" },
    { label: "DGVCL", color: "red" },
  ];

  const getDiscomColorClasses = (
    color: "green" | "purple" | "yellow" | "red",
  ) => {
    const colorClasses = {
      green: {
        bg: "bg-emerald-600",
        hover: "hover:bg-emerald-700",
        disabled: "bg-emerald-400",
      },
      purple: {
        bg: "bg-purple-600",
        hover: "hover:bg-purple-700",
        disabled: "bg-purple-400",
      },
      yellow: {
        bg: "bg-amber-600",
        hover: "hover:bg-amber-700",
        disabled: "bg-amber-400",
      },
      red: {
        bg: "bg-red-600",
        hover: "hover:bg-red-700",
        disabled: "bg-red-400",
      },
    };
    return colorClasses[color];
  };

  const handleGenerateModelDraft = () => {
    if (!canGenerate) {
      toast.error(
        "Please enter Consumer Number before generating the document.",
      );
      return;
    }
    generateModelDraftAgreement(setGeneratingDoc, productRegistration!);
  };

  const handleGenerateTorrent = () => {
    if (!canGenerate) {
      toast.error(
        "Please enter Consumer Number before generating the document.",
      );
      return;
    }
    generateProvisionalAgreementTorrent(
      setGeneratingDoc,
      productRegistration!,
      inverterSize!,
    );
  };

  const handleGeneratePdf = async () => {
    if (!canGenerate) {
      toast.error(
        "Please enter Consumer Number before generating the document.",
      );
      return;
    }

    if (!productRegistration) return;

    try {
      setGeneratingDoc(true);

      const pdfData = {
        ...productRegistration,
        panelMake: isEdit ? panelMake : productRegistration.panelMake,
        panelCapacity: isEdit
          ? panelCapacity
          : productRegistration.panelCapacity,
        numberOfModules: isEdit
          ? numberOfModules
          : productRegistration.numberOfModules,
        inverterMake: isEdit ? inverterMake : productRegistration.inverterMake,
        inverterCapacity: isEdit
          ? inverterCapacity
          : productRegistration.inverterCapacity,
        totalSolarCapacity: isEdit
          ? totalSolarCapacity
          : productRegistration.totalSolarCapacity,
        panelBarcodes: isEdit
          ? parseLines(panelText)
          : productRegistration.panelBarcodes,
        inverterBarcodes: isEdit
          ? parseLines(inverterText)
          : productRegistration.inverterBarcodes,
      };

      const blob = await pdf(
        <InverterSerialNumbersPdf
          productRegistration={pdfData}
          totalSolarCapacity={pdfData.totalSolarCapacity || ""}
        />,
      ).toBlob();

      saveAs(
        blob,
        `${productRegistration.consumerNumber}_inverter-serial-numbers.pdf`,
      );

      toast.success("PDF Generated Successfully");
    } catch (err) {
      console.error("PDF Generation failed", err);
      toast.error("Failed to generate PDF");
    } finally {
      setGeneratingDoc(false);
    }
  };

  const handleGenerateSelfCertification = async () => {
    if (!canGenerate) {
      toast.error(
        "Please enter Consumer Number before generating the document.",
      );
      return;
    }

    if (!productRegistration) return;

    try {
      setGeneratingDoc(true);

      const pdfData = {
        ...productRegistration,
        panelMake: isEdit ? panelMake : productRegistration.panelMake,
        panelCapacity: isEdit
          ? panelCapacity
          : productRegistration.panelCapacity,
        numberOfModules: isEdit
          ? numberOfModules
          : productRegistration.numberOfModules,
        totalSolarCapacity: isEdit
          ? totalSolarCapacity
          : productRegistration.totalSolarCapacity,
        panelVoltage: isEdit ? panelVoltage : productRegistration.panelVoltage,

        inverterMake: isEdit ? inverterMake : productRegistration.inverterMake,
        inverterCapacity: isEdit
          ? inverterCapacity
          : productRegistration.inverterCapacity,
        inverterVoltage: isEdit
          ? inverterVoltage
          : productRegistration.inverterVoltage,

        registrationNumber: isEdit
          ? registrationNumber
          : productRegistration.registrationNumber,

        panelBarcodes: isEdit
          ? parseLines(panelText)
          : productRegistration.panelBarcodes,
        inverterBarcodes: isEdit
          ? parseLines(inverterText)
          : productRegistration.inverterBarcodes,
        installationDate: isEdit
          ? installationDate
            ? new Date(installationDate).toISOString()
            : undefined
          : productRegistration.installationDate,
      };

      const blob = await pdf(
        <SelfCertificationPdf productRegistration={pdfData} />,
      ).toBlob();

      saveAs(
        blob,
        `${productRegistration.consumerNumber}_self-certification.pdf`,
      );

      toast.success("Self Certification PDF Generated");
    } catch (err) {
      console.error("Self Certification Generation failed", err);
      toast.error("Failed to generate PDF");
    } finally {
      setGeneratingDoc(false);
    }
  };

  const handleGenerateDiscom = (discom: Discom) => {
    if (!canGenerate) {
      toast.error(
        "Please enter Consumer Number before generating the document.",
      );
      return;
    }
    generateProvisionalAgreement(
      setGeneratingDoc,
      productRegistration!,
      inverterSize!,
      discom,
    );
  };

  const DisplayField = ({
    label,
    value,
  }: {
    label: string;
    value?: string;
  }) => (
    <div className="bg-gray-50 rounded px-3 py-2 border border-gray-100">
      <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-0.5">
        {label}
      </div>
      <div className="text-sm font-medium text-gray-900 truncate">
        {value || "-"}
      </div>
    </div>
  );

  const getPanelCapacityOptions = () => {
    if (PANEL_CAPACITY_MAPPING[panelMake]) {
      return PANEL_CAPACITY_MAPPING[panelMake];
    }
    return [];
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />

      <div className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden z-10 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600 fill-blue-600" />
              Product Registration
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage installation details and generate agreements
            </p>
          </div>

          <div className="flex items-center gap-3">
            {productRegistration &&
              role !== "account" &&
              role !== "dispatch-lv1" &&
              role !== "dispatch-lv2" && (
                <button
                  onClick={() => setIsEdit((s) => !s)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    isEdit
                      ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  {isEdit ? (
                    <>Cancel Edit</>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11 2H9C4 2 2 4 2 9v6c0 5 2 7 7 7h6c5 0 7-2 7-7v-2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.04 3.02 8.16 10.9c-.3.3-.6.89-.66 1.32l-.43 3.01c-.16 1.09.61 1.85 1.7 1.7l3.01-.43c.42-.06 1.01-.36 1.32-.66l7.88-7.88c1.36-1.36 2-2.94 0-4.94-2-2-3.58-1.36-4.94 0Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Edit Details
                    </>
                  )}
                </button>
              )}

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50/50">
          {!productRegistration ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Copy className="w-8 h-8 text-gray-300" />
              </div>
              <h4 className="text-lg font-medium text-gray-900">
                No Data Available
              </h4>
              <p className="text-sm text-gray-500 max-w-xs mt-1">
                This customer does not have any product registration details
                yet.
              </p>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-500" />
                    Customer Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {isEdit ? (
                      <>
                        <div className="col-span-1">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            Name
                          </label>
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>
                        <div className="col-span-1">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            Consumer No.
                          </label>
                          <input
                            value={consumerNumber}
                            onChange={(e) => setConsumerNumber(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>
                        <div className="col-span-1">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            Registration Number
                          </label>
                          <input
                            value={registrationNumber}
                            onChange={(e) =>
                              setRegistrationNumber(e.target.value)
                            }
                            placeholder="Enter Registration No."
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>
                        <div className="col-span-1">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            Installation Date
                          </label>
                          <input
                            type="date"
                            value={installationDate}
                            onChange={(e) =>
                              setInstallationDate(e.target.value)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>{" "}
                        <div className="col-span-2">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            Address
                          </label>
                          <input
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            Area
                          </label>
                          <input
                            value={area}
                            onChange={(e) => setArea(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <DisplayField
                          label="Customer Name"
                          value={productRegistration.name}
                        />
                        <DisplayField
                          label="Consumer No."
                          value={productRegistration.consumerNumber}
                        />
                        <div className="col-span-1">
                          <DisplayField
                            label="Registration Number"
                            value={
                              (productRegistration as any).registrationNumber
                            }
                          />
                        </div>
                        <div className="col-span-1">
                          <DisplayField
                            label="Installation Date"
                            value={
                              (productRegistration as any).installationDate
                                ? new Date(
                                    (productRegistration as any)
                                      .installationDate,
                                  ).toLocaleDateString("en-GB")
                                : "-"
                            }
                          />
                        </div>{" "}
                        <div className="col-span-2">
                          <DisplayField
                            label="Address"
                            value={productRegistration.address}
                          />
                        </div>
                        <div className="col-span-2">
                          <DisplayField
                            label="Area"
                            value={productRegistration.area}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    Equipment Details
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                        Solar Panels
                      </h5>
                      <div className="grid grid-cols-3 gap-3">
                        {isEdit ? (
                          <>
                            <div className="col-span-1">
                              <SmartInput
                                label="Make"
                                value={panelMake}
                                onChange={(val) => {
                                  setPanelMake(val);
                                  setPanelCapacity("");
                                }}
                                options={PANEL_MAKES}
                                placeholder="Enter Make"
                              />
                            </div>
                            <div className="col-span-1">
                              <SmartInput
                                label="Capacity"
                                value={panelCapacity}
                                onChange={setPanelCapacity}
                                options={getPanelCapacityOptions()}
                                placeholder="Enter Capacity"
                              />
                            </div>
                            <div className="col-span-1">
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                Modules
                              </label>
                              <input
                                value={numberOfModules}
                                type="number"
                                onChange={(e) =>
                                  setNumberOfModules(e.target.value)
                                }
                                placeholder="Count"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                Total Capacity
                              </label>
                              <input
                                value={totalSolarCapacity}
                                onChange={(e) =>
                                  setTotalSolarCapacity(e.target.value)
                                }
                                placeholder="Total Solar Capacity (e.g. 2.00 KW)"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-gray-50"
                              />
                            </div>
                            <div className="col-span-1">
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                Voltage
                              </label>
                              <input
                                value={panelVoltage}
                                onChange={(e) =>
                                  setPanelVoltage(e.target.value)
                                }
                                placeholder="Voltage"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <DisplayField
                              label="Make"
                              value={productRegistration.panelMake}
                            />
                            <DisplayField
                              label="Capacity"
                              value={productRegistration.panelCapacity}
                            />
                            <DisplayField
                              label="Modules"
                              value={productRegistration.numberOfModules}
                            />
                            <div className="col-span-2">
                              <DisplayField
                                label="Total Capacity"
                                value={
                                  productRegistration.totalSolarCapacity || "-"
                                }
                              />
                            </div>
                            <div className="col-span-1">
                              <DisplayField
                                label="Voltage"
                                value={
                                  (productRegistration as any).panelVoltage
                                }
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="h-px bg-gray-100" />

                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                        Inverter
                      </h5>
                      <div className="grid grid-cols-2 gap-3">
                        {isEdit ? (
                          <>
                            <div className="col-span-1">
                              <SmartInput
                                label="Make"
                                value={inverterMake}
                                onChange={setInverterMake}
                                options={INVERTER_MAKES}
                                placeholder="Enter Make"
                              />
                            </div>
                            <div className="col-span-1">
                              <SmartInput
                                label="Capacity"
                                value={inverterCapacity}
                                onChange={setInverterCapacity}
                                options={INVERTER_CAPACITIES}
                                placeholder="Enter Capacity"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                Voltage
                              </label>
                              <input
                                value={inverterVoltage}
                                onChange={(e) =>
                                  setInverterVoltage(e.target.value)
                                }
                                placeholder="Voltage"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <DisplayField
                              label="Make"
                              value={productRegistration.inverterMake}
                            />
                            <DisplayField
                              label="Capacity"
                              value={productRegistration.inverterCapacity}
                            />
                            <div className="col-span-2">
                              <DisplayField
                                label="Voltage"
                                value={
                                  (productRegistration as any).inverterVoltage
                                }
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-500" />
                    Geo Location Photo
                  </h4>
                  <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-8 h-8 bg-white rounded-md border border-gray-200 flex items-center justify-center text-gray-400">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium truncate">
                        {file?.name ||
                          productRegistration.geoPhoto?.name ||
                          "No photo uploaded"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {!isEdit && productRegistration.geoPhoto?.link && (
                        <button
                          onClick={handleDownload}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <FileDown className="w-3.5 h-3.5" />
                          Download
                        </button>
                      )}
                      {isEdit && (
                        <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors">
                          Change
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6 flex flex-col">
                <div className="bg-gradient-to-br from-indigo-50 to-white p-5 rounded-xl border border-indigo-100 shadow-sm">
                  <h4 className="text-sm font-bold text-indigo-900 mb-3 flex items-center gap-2">
                    <FileDown className="w-4 h-4 text-indigo-600" />
                    Generate Agreements
                  </h4>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      onClick={handleGenerateModelDraft}
                      disabled={generatingDoc}
                      className={`py-2 px-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                        generatingDoc
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:text-blue-600 hover:shadow-sm"
                      }`}
                    >
                      {generatingDoc ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Model Draft"
                      )}
                    </button>
                    <button
                      onClick={handleGenerateTorrent}
                      disabled={generatingDoc}
                      className={`py-2 px-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                        generatingDoc
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:text-indigo-600 hover:shadow-sm"
                      }`}
                    >
                      {generatingDoc ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Torrent Agmt."
                      )}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      onClick={handleGeneratePdf}
                      disabled={generatingDoc}
                      className={`py-2 px-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                        generatingDoc
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:text-red-600 hover:shadow-sm"
                      }`}
                    >
                      {generatingDoc ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <FileText className="w-3.5 h-3.5" />
                          Panel Sr No.
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleGenerateSelfCertification}
                      disabled={generatingDoc}
                      className={`py-2 px-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                        generatingDoc
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "bg-white text-gray-700 border-gray-200 hover:border-green-300 hover:text-green-600 hover:shadow-sm"
                      }`}
                    >
                      {generatingDoc ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <BadgeCheck className="w-3.5 h-3.5" />
                          Self Cert.
                        </>
                      )}
                    </button>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase font-bold text-indigo-300 mb-2 tracking-wider">
                      DISCOM Provisional
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {discomButtons.map((btn) => {
                        const colors = getDiscomColorClasses(btn.color);
                        return (
                          <button
                            key={btn.label}
                            onClick={() => handleGenerateDiscom(btn.label)}
                            disabled={generatingDoc}
                            className={`py-2 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95 ${
                              generatingDoc
                                ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                : `${colors.bg} text-white ${colors.hover}`
                            }`}
                          >
                            {btn.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col min-h-[300px]">
                  <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Copy className="w-4 h-4 text-gray-500" />
                    Barcodes
                  </h4>

                  <div className="flex flex-col gap-4 flex-1">
                    <div className="flex flex-col flex-1 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="text-xs font-semibold text-gray-600 mb-2 flex justify-between items-center">
                        <span>Panel</span>
                        <span className="bg-gray-200 text-gray-600 px-1.5 rounded text-[10px]">
                          {parseLines(panelText).length}
                        </span>
                      </div>

                      {isEdit &&
                        addBarcodeInput("panel", panelText, setPanelText)}

                      <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 max-h-[250px]">
                        {parseLines(panelText).map((barcode, i) => (
                          <div
                            key={i}
                            className="bg-white border border-gray-200 rounded px-2 py-1.5 text-xs font-mono text-gray-600 flex items-center justify-between group hover:border-blue-300 transition-colors"
                          >
                            <span className="truncate mr-2">{barcode}</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => copyToClipboard(barcode)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              {isEdit && (
                                <button
                                  onClick={() => removeBarcode("panel", i)}
                                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col flex-1 bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="text-xs font-semibold text-gray-600 mb-2 flex justify-between items-center">
                        <span>Inverter</span>
                        <span className="bg-gray-200 text-gray-600 px-1.5 rounded text-[10px]">
                          {parseLines(inverterText).length}
                        </span>
                      </div>

                      {isEdit &&
                        addBarcodeInput(
                          "inverter",
                          inverterText,
                          setInverterText,
                        )}

                      <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 max-h-[250px]">
                        {parseLines(inverterText).map((barcode, i) => (
                          <div
                            key={i}
                            className="bg-white border border-gray-200 rounded px-2 py-1.5 text-xs font-mono text-gray-600 flex items-center justify-between group hover:border-green-300 transition-colors"
                          >
                            <span className="truncate mr-2">{barcode}</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => copyToClipboard(barcode)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              {isEdit && (
                                <button
                                  onClick={() => removeBarcode("inverter", i)}
                                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {isEdit && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white">
            <Button
              type={ButtonType.Cancel}
              onClick={cancelEdit}
              className="text-xs px-4"
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              type={ButtonType.Submit}
              onClick={handleSave}
              className={`text-xs px-6 ${
                loading ? "opacity-70 pointer-events-none" : ""
              }`}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Updating...
                </div>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowRegisteredProductModal;
